'use client'

import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { updateUser } from '@/app/actions/user'
import { Text } from '@/blocks/Form/Text'
import { Button } from '@/components/ui/button'
// import { getClientSideURL } from '@/utilities/getURL'
import { User } from '@/payload-types'
import { formValidation } from '@/utilities/formValidation'

import { ProfileSteps } from './ProfileStep'

import type { FieldValues, UseFormRegister } from 'react-hook-form'

type SocialFormValues = {
  facebook?: string | null
  x?: string | null
  instagram?: string | null
  linkedin?: string | null
  tiktok?: string | null
  youtube?: string | null
}

export const SocialForm = ({
  user,
  setOpenDrawer,
  setCurrentProfileStep,
  onUserUpdated,
}: {
  user: User
  setOpenDrawer: (open: boolean) => void
  setCurrentProfileStep: (step: ProfileSteps) => void
  onUserUpdated?: (updatedUser?: User) => Promise<void>
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const methods = useForm<SocialFormValues>({
    defaultValues: {
      facebook: user.socials?.socialLinks?.facebook ?? '',
      x: user.socials?.socialLinks?.x ?? '',
      instagram: user.socials?.socialLinks?.instagram ?? '',
      linkedin: user.socials?.socialLinks?.linkedin ?? '',
      tiktok: user.socials?.socialLinks?.tiktok ?? '',
      youtube: user.socials?.socialLinks?.youtube ?? '',
    },
  })

  const onSubmit = async (data: SocialFormValues) => {
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      const newSocials: User['socials'] = {
        socialLinks: {
          facebook: data.facebook ?? '',
          x: data.x ?? '',
          instagram: data.instagram ?? '',
          linkedin: data.linkedin ?? '',
          tiktok: data.tiktok ?? '',
          youtube: data.youtube ?? '',
        },
      }
      const res = await updateUser({
        ...user,
        socials: newSocials,
      })

      if (res.status === 'error') {
        setSubmitError(res.message)
        return
      }

      const updatedUser = res.data

      if (updatedUser?.socials?.socialLinks) {
        methods.reset({
          facebook: updatedUser.socials.socialLinks.facebook ?? '',
          x: updatedUser.socials.socialLinks.x ?? '',
          instagram: updatedUser.socials.socialLinks.instagram ?? '',
          linkedin: updatedUser.socials.socialLinks.linkedin ?? '',
          tiktok: updatedUser.socials.socialLinks.tiktok ?? '',
          youtube: updatedUser.socials.socialLinks.youtube ?? '',
        })
      }
      setOpenDrawer(false)
      setCurrentProfileStep('location')
      await onUserUpdated?.(updatedUser)
      toast.success('Profile updated successfully')
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred while submitting the form',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form id="social-form" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="m-auto my-6 max-w-xl">
          <h2 className="font-family-body text-2xl font-semibold text-foreground">Social</h2>
          <p className="mt-3 font-body text-card-foreground">
            Connect your social profiles so people can find and follow you more easily. Only link
            the platforms you use.{' '}
          </p>
          <div className="my-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="col-span-2 md:col-span-1">
              <Text
                name="facebook"
                label="Facebook"
                blockType="text"
                placeholder="Enter Facebook"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.websiteUrl}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="x"
                label="X (Twitter)"
                blockType="text"
                placeholder="Enter X (Twitter)"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.websiteUrl}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="instagram"
                label="Instagram"
                blockType="text"
                placeholder="Enter Instagram"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.websiteUrl}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="linkedin"
                label="LinkedIn"
                blockType="text"
                placeholder="Enter LinkedIn"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.websiteUrl}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="tiktok"
                label="TikTok"
                blockType="text"
                placeholder="Enter TikTok"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.websiteUrl}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="youtube"
                label="YouTube"
                blockType="text"
                placeholder="Enter YouTube"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.websiteUrl}
              />
            </div>
            <Button
              type="submit"
              className="col-span-2 block text-center"
              labelClassName="w-fit mx-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {submitError && (
            <p className="m-auto rounded-md bg-red-50 p-3 text-center text-sm text-red-500">
              {submitError}
            </p>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
